import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import CodeIcon from '@mui/icons-material/Code';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ImageIcon from '@mui/icons-material/Image';
import SaveIcon from '@mui/icons-material/Save';
import DescriptionIcon from '@mui/icons-material/Description';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import { SiGit } from 'react-icons/si';

const ToolbarButton = ({ title, onClick, active, children, style = {} }) => {
  const { theme } = useTheme();
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        background: active ? theme.toolbar.buttonBackgroundActive : theme.toolbar.buttonBackground,
        color: theme.toolbar.buttonText,
        border: `1px solid ${theme.toolbar.buttonBorder}`,
        borderRadius: 6,
        padding: '6px 8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontSize: '14px',
        minWidth: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.target.style.backgroundColor = theme.name === 'light' ? '#f1f5f9' : '#374151';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.target.style.backgroundColor = theme.toolbar.buttonBackground;
        }
      }}
    >
      {children}
    </button>
  );
};

const ToolbarBpmn = ({
  zoomIn,
  zoomOut,
  fitToBoard,
  onSaveDiagram,
  onLoadDiagram,
  currentDiagramId,
  _currentDiagramMeta,
  _zoom,
  topOffset = 0,
  onExportImage,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  showCodePanel = false,
  onToggleCodePanel,
  onSyncToCode,
  onPublishDiagram,
  isPublishing = false,
  onShowHistory,
  diagramGitlabPath,
  onBeforeNavigate,
}) => {
  const { theme, toggleTheme, currentTheme } = useTheme();
  const navigate = useNavigate();
  const [currentDiagramMeta, setCurrentDiagramMeta] = useState(_currentDiagramMeta);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportButtonRef = useRef(null);

  useEffect(() => {
    setCurrentDiagramMeta(_currentDiagramMeta);
  }, [_currentDiagramMeta]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportButtonRef.current && !exportButtonRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const iconStyle = { width: 18, height: 18 };
  const guardedNavigate = (target) => {
    if (typeof onBeforeNavigate === 'function' && !onBeforeNavigate()) return;
    navigate(target);
  };

  return (
    <div style={{
      position: 'absolute',
      top: topOffset,
      left: 0,
      right: 0,
      height: 56,
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: theme.toolbar.background,
      borderBottom: `1px solid ${theme.toolbar.border}`,
      padding: '0 16px',
      boxShadow: theme.toolbar.shadow
    }}>
      {/* Левая секция */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ToolbarButton title="Назад" onClick={() => guardedNavigate(-1)}>
          <ArrowBackIcon style={iconStyle} />
        </ToolbarButton>

        <div style={{
          fontSize: 18,
          fontWeight: 'bold',
          color: theme.toolbar.text,
          marginRight: 16
        }}>
          BPMN Editor
        </div>

        <ToolbarButton title="Новая диаграмма">
          <DescriptionIcon style={iconStyle} />
        </ToolbarButton>

        <ToolbarButton title="Сохранить диаграмму (Ctrl+S)" onClick={onSaveDiagram}>
          <SaveIcon style={iconStyle} />
        </ToolbarButton>

        {onPublishDiagram && (
          <ToolbarButton
            title={isPublishing ? 'Публикуется...' : 'Опубликовать в GitLab'}
            onClick={onPublishDiagram}
            style={{ color: isPublishing ? undefined : '#f05032', borderColor: isPublishing ? undefined : '#f05032', opacity: isPublishing ? 0.6 : 1 }}
          >
            <SiGit style={iconStyle} />
          </ToolbarButton>
        )}

        {onShowHistory && diagramGitlabPath && (
          <ToolbarButton
            title="История версий"
            onClick={onShowHistory}
            style={{ color: '#a6e3a1', borderColor: '#40a02b' }}
          >
            <HistoryIcon style={iconStyle} />
          </ToolbarButton>
        )}

        {onExportImage && (
          <div style={{ position: 'relative' }} ref={exportButtonRef}>
            <ToolbarButton
              title="Экспорт диаграммы"
              onClick={() => setShowExportMenu(!showExportMenu)}
              active={showExportMenu}
            >
              <ImageIcon style={iconStyle} />
            </ToolbarButton>
            {showExportMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: 4,
                background: theme.toolbar.background,
                border: `1px solid ${theme.toolbar.border}`,
                borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 3000,
                minWidth: 120
              }}>
                {['svg', 'png', 'jpg'].map(fmt => (
                  <button
                    key={fmt}
                    onClick={() => {
                      onExportImage(fmt);
                      setShowExportMenu(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 16px',
                      background: 'transparent',
                      border: 'none',
                      color: theme.toolbar.buttonText,
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textTransform: 'uppercase'
                    }}
                    onMouseEnter={(e) => e.target.style.background = theme.name === 'light' ? '#f1f5f9' : '#374151'}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                  >
                    {fmt}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{
          width: 1,
          height: 24,
          backgroundColor: theme.toolbar.border,
          margin: '0 8px'
        }} />

        {/* Undo/Redo */}
        <ToolbarButton
          title="Назад (Ctrl+Z)"
          onClick={onUndo}
          style={{ opacity: canUndo ? 1 : 0.5, cursor: canUndo ? 'pointer' : 'not-allowed' }}
        >
          ↶
        </ToolbarButton>

        <ToolbarButton
          title="Вперёд (Ctrl+Shift+Z)"
          onClick={onRedo}
          style={{ opacity: canRedo ? 1 : 0.5, cursor: canRedo ? 'pointer' : 'not-allowed' }}
        >
          ↷
        </ToolbarButton>

        <div style={{
          width: 1,
          height: 24,
          backgroundColor: theme.toolbar.border,
          margin: '0 8px'
        }} />

        {onToggleCodePanel && (
          <>
            <div style={{ width: 1, height: 24, backgroundColor: theme.toolbar.border, margin: '0 8px' }} />
            <ToolbarButton
              title="DSL / Code panel"
              onClick={onToggleCodePanel}
              active={showCodePanel}
            >
              <CodeIcon style={iconStyle} />
            </ToolbarButton>
            {onSyncToCode && (
              <ToolbarButton
                title="Синхронизировать код"
                onClick={onSyncToCode}
                style={{ fontSize: 11, padding: '6px 10px', gap: 4 }}
              >
                \u2190 Sync to Code
              </ToolbarButton>
            )}
          </>
        )}
      </div>

      {/* Центральная секция - Навигация */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ToolbarButton title="Увеличить" onClick={zoomIn}>
          <ZoomInIcon style={iconStyle} />
        </ToolbarButton>

        <ToolbarButton title="Уменьшить" onClick={zoomOut}>
          <ZoomOutIcon style={iconStyle} />
        </ToolbarButton>

        <ToolbarButton title="Вместить все" onClick={fitToBoard}>
          <CenterFocusStrongIcon style={iconStyle} />
        </ToolbarButton>
      </div>

      {/* Правая секция */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ToolbarButton
          title={`Тема: ${currentTheme}`}
          onClick={toggleTheme}
        >
          {currentTheme === 'dark' ? (
            <LightModeIcon style={iconStyle} />
          ) : (
            <DarkModeIcon style={iconStyle} />
          )}
        </ToolbarButton>

        {currentDiagramMeta?.linkedSystems && currentDiagramMeta.linkedSystems.length > 0 && (
          <div style={{
            color: theme.toolbar.text,
            fontSize: 12,
            opacity: 0.7,
            marginLeft: 8
          }}>
            {currentDiagramMeta.linkedSystems.length} систем(ы)
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolbarBpmn;
